export default function TradeInGuide() {
  const tips = [
    {
      icon: 'ri-brush-line',
      title: 'Clean Inside and Out',
      description: 'A clean vehicle makes a better first impression. Wash, vacuum, and remove personal items. Consider professional detailing for maximum impact.',
      impact: '+5-10% value'
    },
    {
      icon: 'ri-file-list-3-line',
      title: 'Gather All Documentation',
      description: 'Have your title, registration, service records, and warranty information ready. Complete maintenance history shows you cared for the vehicle.',
      impact: '+3-7% value'
    },
    {
      icon: 'ri-tools-line',
      title: 'Fix Minor Issues',
      description: 'Replace burned-out bulbs, fix small dents, repair windshield chips. Small fixes can significantly boost perceived value.',
      impact: '+5-15% value'
    },
    {
      icon: 'ri-file-shield-2-line',
      title: 'Get a Vehicle History Report',
      description: 'A clean Carfax or AutoCheck report proves your vehicle\'s history. No accidents or major issues? That\'s worth advertising.',
      impact: '+10-20% value'
    },
    {
      icon: 'ri-calendar-check-line',
      title: 'Time It Right',
      description: 'Trade in before major repairs are needed. Spring and summer typically see higher demand for most vehicles.',
      impact: '+5-10% value'
    },
    {
      icon: 'ri-search-line',
      title: 'Know Your Vehicle\'s Value',
      description: 'Research current market prices on KBB, NADA, and local listings. Walk in informed so you can negotiate confidently.',
      impact: 'Negotiation power'
    }
  ];

  const mistakes = [
    {
      icon: 'ri-store-2-line',
      color: 'text-red-500',
      bg: 'bg-red-50',
      mistake: 'Not shopping around',
      why: 'The first offer is rarely the best. Get multiple appraisals to ensure you\'re getting fair value.'
    },
    {
      icon: 'ri-timer-flash-line',
      color: 'text-orange-500',
      bg: 'bg-orange-50',
      mistake: 'Mentioning you need to sell quickly',
      why: 'This weakens your negotiating position. Never show desperation, even if you\'re in a hurry.'
    },
    {
      icon: 'ri-hand-coin-line',
      color: 'text-amber-500',
      bg: 'bg-amber-50',
      mistake: 'Accepting the first offer',
      why: 'Dealers expect negotiation. A polite counteroffer often results in a better deal.'
    },
    {
      icon: 'ri-eye-off-line',
      color: 'text-rose-500',
      bg: 'bg-rose-50',
      mistake: 'Hiding vehicle issues',
      why: 'Undisclosed problems will be found during inspection and hurt your credibility. Honesty builds trust.'
    },
    {
      icon: 'ri-bank-card-line',
      color: 'text-red-600',
      bg: 'bg-red-50',
      mistake: 'Trading in with negative equity',
      why: 'If you owe more than the car is worth, that debt rolls into your new loan. Consider paying down first.'
    },
    {
      icon: 'ri-inbox-unarchive-line',
      color: 'text-orange-600',
      bg: 'bg-orange-50',
      mistake: 'Forgetting to remove personal items',
      why: 'Check the glove box, trunk, and under seats. Once you sign, it\'s gone for good.'
    }
  ];

  return (
    <section className="mb-16">
      <div className="text-center mb-12">
        <span className="text-xs uppercase tracking-wider text-[#2C5F2D] font-medium">Expert Tips</span>
        <h2 className="text-3xl font-bold text-gray-900 mt-2 mb-3">
          How to Get the Best Trade-In Value
        </h2>
        <p className="text-sm text-gray-600 max-w-2xl mx-auto">
          Follow these proven strategies to maximize your trade-in offer and avoid common pitfalls.
        </p>
      </div>

      {/* Tips to Maximize Value */}
      <div className="mb-12">
        <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
          <div className="w-8 h-8 flex items-center justify-center bg-[#2C5F2D] rounded-lg">
            <i className="ri-arrow-up-circle-line text-white"></i>
          </div>
          6 Ways to Maximize Your Trade-In Value
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {tips.map((tip, index) => (
            <div key={index} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#F0F7F0] rounded-xl flex-shrink-0">
                  <i className={`${tip.icon} text-[#2C5F2D] text-xl`}></i>
                </div>
                <div className="flex-1">
                  <h4 className="text-base font-bold text-gray-900 mb-1">{tip.title}</h4>
                  <span className="inline-block text-xs font-semibold text-[#2C5F2D] bg-[#F0F7F0] px-2 py-1 rounded-full">
                    {tip.impact}
                  </span>
                </div>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">{tip.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Common Mistakes */}
      <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-8 border border-red-200">
        <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
          <div className="w-8 h-8 flex items-center justify-center bg-red-600 rounded-lg">
            <i className="ri-alert-line text-white"></i>
          </div>
          Common Trade-In Mistakes to Avoid
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {mistakes.map((item, index) => (
            <div key={index} className="bg-white rounded-xl p-5 border border-red-100 hover:shadow-md transition-shadow">
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 flex items-center justify-center rounded-xl flex-shrink-0 ${item.bg}`}>
                  <i className={`${item.icon} ${item.color} text-2xl`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-bold text-red-500 bg-red-50 border border-red-200 rounded-full px-2 py-0.5">
                      #{index + 1} Mistake
                    </span>
                  </div>
                  <h4 className="text-sm font-bold text-gray-900 mb-1">{item.mistake}</h4>
                  <p className="text-xs text-gray-500 leading-relaxed">{item.why}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Pro Tip */}
      <div className="mt-8 bg-gradient-to-r from-[#2C5F2D] to-[#3a7a3c] rounded-2xl p-6 text-white">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-xl flex-shrink-0">
            <i className="ri-star-line text-[#E8B84B] text-xl"></i>
          </div>
          <div>
            <h4 className="text-lg font-bold mb-2">Pro Tip: Timing Matters</h4>
            <p className="text-sm text-white/90 leading-relaxed">
              Trade in your vehicle <strong>before</strong> it needs major repairs (transmission, engine, etc.). Once a vehicle needs expensive work, its value drops significantly. If you're approaching 100k miles or know repairs are coming, now is the time to trade.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
